import { PageContainer } from '@/components/Atoms/Layout/PageContainer'
import { PageHeader } from '@/components/Atoms/Text/PageHeader'
import { Card, CardContent } from '@/components/Card'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { ClientDataTable } from '@/components/DataTable/ClientDataTable'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { Role, useRoles } from '@/hooks/queries/useRoles'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { PAGE_SIZE } from '@repo/constants'
import { getRoleDescription, ROLE_PERMISSIONS, RoleName, UserPermission } from '@repo/permissions'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RoleDialog } from './-components/RoleDialog'

const getPermissionLabelsFromRole = (role: Role): UserPermission[] => {
  const permissions = ROLE_PERMISSIONS[role.name as keyof typeof ROLE_PERMISSIONS]
  if (!permissions) return []

  return Object.entries(permissions).reduce<UserPermission[]>((acc, [key, value]) => {
    if (value === true) acc.push(key as UserPermission)

    return acc
  }, [])
}

export const Route = createFileRoute('/_auth/_modules/_app/admin/roles/')({
  component: RolesPage,
})

type RolesTableRow = Role & { permissions: UserPermission[]; description: string } & {
  isUnsaved?: boolean
}

function RolesPage() {
  const { t } = useTranslation(['admin', 'structural'])

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })

  const { data: rolesData, isPending: areRolesPending, error: rolesError } = useRoles()

  const roles =
    rolesData
      ?.filter((role) => !role.pendingDeletion)
      .map(
        (role) =>
          ({
            ...role,
            permissions: getPermissionLabelsFromRole(role),
            description: t(getRoleDescription(role.name as RoleName), { ns: 'structural' }),
          }) as RolesTableRow,
      ) ?? []

  const columns: ColumnDef<RolesTableRow>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => <TableHeader column={column} />,
      enableHiding: false,
      cell: ({ row }) => {
        const role = row.original
        return <span>{t(`role.${role.name}`, { ns: 'structural' })}</span>
      },
      meta: {
        displayTitle: t('rolesDataTable.role'),
      },
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: ({ column }) => <TableHeader column={column} />,
      enableHiding: false,
      cell: ({ row }) => {
        const role = row.original
        return <span>{role.description}</span>
      },
      meta: {
        displayTitle: t('rolesDataTable.description'),
      },
    },
    {
      id: 'roleDetails',
      cell: ({ row }) => {
        const role = row.original
        return <RoleDialog role={role} />
      },
      meta: {
        hideInColumnsMenu: true,
        cellClassName: 'w-0',
      },
    },
  ]

  return (
    <PageContainer>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.ROLES}
        title={t('tutorials.dialog.roles.title')}
        guideUrl="/admin/roles"
      >
        {t('tutorials.dialog.roles.description')}
      </IntroductionDialog>

      <PageHeader>{t('rolesPage.header')}</PageHeader>

      <Card>
        <CardContent className="pt-6">
          <ClientDataTable
            columns={columns}
            data={roles}
            isPending={areRolesPending}
            error={rolesError}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
